// ─────────────────────────────────────────────────────────────
// BuddiesHR, Build vs. Buy page sections (new brand)
// Reuses BuildVsBuy + FooterCTA from landing sections.js; adds a
// dedicated hero, AI objection, true-cost table, 12-month timeline,
// "iceberg" scope, and a switcher quote.
// ─────────────────────────────────────────────────────────────

/* eslint-disable react/no-unescaped-entities */
import React from "react";
import Link from "next/link";

// ═══ PAGE HERO ═══
function BvBHero() {
  return (
    <section className="bh-section-hero" style={{ position: 'relative', paddingBottom: 32, overflow: 'hidden' }}>
      <div className="bh-blob" style={{
        position: 'absolute', top: -160, right: -180, width: 620, height: 620,
        background: 'radial-gradient(circle at 40% 40%, var(--bh-sun-200), var(--bh-sun-100) 55%, transparent 72%)',
        zIndex: 0, filter: 'blur(2px)',
      }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 920, margin: '0 auto', textAlign: 'center' }}>
        <div className="bh-chip" style={{ marginBottom: 22 }}>
          <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--bh-coral-400)' }} />
          🛠️ Build vs. buy · An honest essay
        </div>
        <h1 style={{ fontSize: 'clamp(38px, 5.6vw, 70px)', letterSpacing: '-0.035em', lineHeight: 1.02, margin: 0 }}>
          "I'll just have Claude<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>build it this afternoon."</span>
        </h1>
        <p style={{ fontSize: 18, color: 'var(--bh-ink-soft)', marginTop: 22, lineHeight: 1.55, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
          Honestly? It probably can. Every team has this conversation now, most of our customers had it too, before they found us.
          This is the long version of the chat: what the afternoon actually buys you, and what the next twelve months cost.
        </p>
        <div style={{ marginTop: 28, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="#build-vs-buy" className="bh-btn primary" style={{ textDecoration: 'none' }}>Read the three questions <span className="bh-arrow">→</span></a>
          <Link href="/" className="bh-btn ghost" style={{ textDecoration: 'none' }}>← Back to BuddiesHR</Link>
        </div>
        <div style={{ marginTop: 22, fontSize: 13, color: 'var(--bh-ink-mute)' }}>
          ~6 min read · Based on conversations with 80+ teams that built before they bought
        </div>
      </div>
    </section>
  );
}

// ═══ THE AI OBJECTION ═══
// "Claude writes the code now", what AI compresses, and what it doesn't.
function BvBAIBuild() {
  const compresses = [
    'Writing the v1, genuinely, an afternoon',
    'Block Kit & Adaptive Card boilerplate',
    'The Postgres schema and migrations',
    'Even the unit tests',
  ];
  const remains = [
    'Being on-call when birthdays don’t post',
    'The Slack deprecation that lands in month 6',
    'Security review of code nobody on the team wrote',
    'HRIS credentials, syncs and conflicts, forever',
    'HR’s feature requests, every quarter',
    'Ownership, when the person who prompted it leaves',
  ];
  return (
    <section className="bh-section" style={{ maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 44px' }}>
        <div className="bh-chip" style={{ marginBottom: 14 }}>🤖 “But Claude writes the code now”</div>
        <h2 style={{ fontSize: 'clamp(30px, 4.2vw, 50px)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0 }}>
          AI made the build nearly free.<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>The build was never the bill.</span>
        </h2>
        <p style={{ fontSize: 16, color: 'var(--bh-ink-soft)', marginTop: 14, lineHeight: 1.5 }}>
          We agree: Claude will write you a working kudos bot before your coffee is cold. The question was never
          “can we produce the code?”, it was “who owns it in month eight?”
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 980, margin: '0 auto' }}>
        <div style={{
          background: '#fff', border: '1.5px solid var(--bh-line)',
          borderRadius: 'var(--bh-r-lg)', padding: '28px 30px',
          boxShadow: 'var(--bh-shadow-sm)',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
            color: 'var(--app-clappy)', marginBottom: 14,
          }}>What Claude compresses</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
            {compresses.map((t, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 15, lineHeight: 1.5, color: 'var(--bh-ink)' }}>
                <span style={{ color: 'var(--app-clappy)', fontWeight: 700 }}>✓</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p style={{ margin: '18px 0 0', fontSize: 13.5, color: 'var(--bh-ink-mute)', lineHeight: 1.5 }}>
            i.e. the part that was already the cheapest line on the estimate.
          </p>
        </div>

        <div style={{
          background: '#fff', border: '1.5px solid var(--bh-coral-200)',
          borderRadius: 'var(--bh-r-lg)', padding: '28px 30px',
          boxShadow: 'var(--bh-shadow-sm)',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
            color: 'var(--bh-coral-400)', marginBottom: 14,
          }}>What still lands on you</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
            {remains.map((t, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 15, lineHeight: 1.5, color: 'var(--bh-ink)' }}>
                <span style={{ color: 'var(--bh-coral-400)', fontWeight: 700 }}>→</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p style={{
        maxWidth: 720, margin: '40px auto 0', textAlign: 'center',
        fontFamily: 'var(--bh-font-display)', fontStyle: 'italic',
        fontSize: 20, lineHeight: 1.5, color: 'var(--bh-ink-soft)',
      }}>
        The cheaper the v1 gets, the easier it is to end up owning software <span style={{ color: 'var(--bh-coral-400)' }}>no one wrote.</span>
      </p>
    </section>
  );
}

// ═══ TRUE COST ═══
function BvBTrueCost() {
  const rows = [
    {
      story: 'A weekend Slack bot',
      reality: '5–8 days for a v1 that handles only happy paths',
      hidden: 'Time-zones, retries, Slack rate limits, error states, "what if the channel was archived?"',
      mult: '~3×',
    },
    {
      story: '"We\'ll just store it in Postgres"',
      reality: 'A schema, migrations, a back-office UI, GDPR exports & deletion',
      hidden: 'Permissions model, audit log, "who can see what" arguments in three meetings',
      mult: '~4×',
    },
    {
      story: '"We\'ll add Teams later"',
      reality: 'A near-full rewrite, Teams adaptive cards are not Slack Block Kit',
      hidden: 'Tenant install flow, MS Graph permissions, separate review by IT',
      mult: '~2×',
    },
    {
      story: 'Maintenance is 1 hour a week',
      reality: 'Slack ships breaking changes ~quarterly; API deprecations land cold',
      hidden: 'On-call for HR tooling, internal Jira, the engineer who left',
      mult: 'ongoing',
    },
  ];
  return (
    <section className="bh-section" style={{
      background: 'var(--bh-cream-deep)',
      borderTop: '1px solid var(--bh-line)',
      borderBottom: '1px solid var(--bh-line)',
    }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 40px' }}>
          <div className="bh-chip" style={{ marginBottom: 14 }}>💸 The estimate vs. the bill</div>
          <h2 style={{ fontSize: 'clamp(30px, 4.2vw, 50px)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0 }}>
            The estimate is always the v1.<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>The bill is everything around it.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'var(--bh-ink-soft)', marginTop: 14, lineHeight: 1.5 }}>
            We asked four engineering leads to compare what they pitched to leadership vs. what they actually spent. Composite, rounded honest.
          </p>
        </div>

        <div style={{
          background: '#fff',
          border: '1.5px solid var(--bh-line)',
          borderRadius: 'var(--bh-r-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--bh-shadow-sm)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.4fr 1.6fr 100px',
            padding: '14px 24px',
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
            color: 'var(--bh-ink-mute)',
            borderBottom: '1px solid var(--bh-line)',
            background: 'var(--bh-cream)',
          }}>
            <div>The pitch</div>
            <div>What it actually was</div>
            <div>What no one budgeted</div>
            <div style={{ textAlign: 'right' }}>Estimate ×</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1.4fr 1.6fr 100px',
              padding: '20px 24px',
              gap: 16,
              alignItems: 'start',
              borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--bh-line)',
              fontSize: 14.5, lineHeight: 1.5,
            }}>
              <div style={{ fontFamily: 'var(--bh-font-display)', fontStyle: 'italic', color: 'var(--bh-ink)', fontSize: 16 }}>"{r.story}"</div>
              <div style={{ color: 'var(--bh-ink)' }}>{r.reality}</div>
              <div style={{ color: 'var(--bh-ink-soft)', fontFamily: 'var(--bh-font-mono)', fontSize: 12.5 }}>{r.hidden}</div>
              <div style={{ textAlign: 'right' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--bh-coral-100, #FFE3DA)',
                  color: 'var(--bh-coral-600, var(--bh-coral-400))',
                  fontFamily: 'var(--bh-font-mono)',
                  fontSize: 12, fontWeight: 700,
                  border: '1px solid var(--bh-coral-200)',
                }}>{r.mult}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ 12-MONTH TIMELINE ═══
function BvBTimeline() {
  const months = [
    { m: 'Month 1', t: 'Excitement', d: 'Claude writes the MVP in a day. Slack messages get sent. Team posts a screenshot in #demos.', mood: 'green' },
    { m: 'Month 2', t: 'First edge cases', d: 'Birthday at midnight UTC pinging the wrong country. Three Jira tickets. Still excited.', mood: 'green' },
    { m: 'Month 3', t: 'Feature creep', d: 'HR asks: "could it also do anniversaries? And kudos? And a survey? And export to Notion?"', mood: 'amber' },
    { m: 'Month 4', t: 'Teams ask', d: 'A new acquisition uses Microsoft Teams. The bot doesn\'t. Estimate doubles.', mood: 'amber' },
    { m: 'Month 6', t: 'The Slack API change', d: 'Block Kit deprecation. Half a sprint to migrate. No new features that month.', mood: 'red' },
    { m: 'Month 8', t: 'The original engineer leaves', d: 'Knowledge transfer is "the README and one Loom." Velocity on the bot drops to zero.', mood: 'red' },
    { m: 'Month 10', t: 'Security review', d: 'IT asks where employee data lives. Answer is "in a Postgres on Heroku." Meeting follows.', mood: 'red' },
    { m: 'Month 12', t: 'The conversation', d: '"Should we just buy something for this?" Most teams arrive here within the first year.', mood: 'ink' },
  ];
  const moodColors = {
    green: 'var(--app-clappy)',
    amber: 'var(--bh-sun-500)',
    red: 'var(--bh-coral-400)',
    ink:   'var(--bh-ink)',
  };
  return (
    <section className="bh-section" style={{ maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 48px' }}>
        <div className="bh-chip" style={{ marginBottom: 14 }}>📅 A year in the life</div>
        <h2 style={{ fontSize: 'clamp(30px, 4.2vw, 50px)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0 }}>
          What actually happens<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>after the MVP ships.</span>
        </h2>
      </div>
      <ol style={{
        position: 'relative',
        listStyle: 'none', padding: 0, margin: 0,
        display: 'grid', gap: 12,
      }}>
        {months.map((mo, i) => (
          <li key={i} style={{
            display: 'grid',
            gridTemplateColumns: '110px 16px 1fr',
            gap: 18,
            alignItems: 'start',
            background: '#fff',
            border: '1.5px solid var(--bh-line)',
            borderRadius: 'var(--bh-r-lg)',
            padding: '18px 22px',
            boxShadow: 'var(--bh-shadow-sm)',
          }}>
            <div style={{
              fontFamily: 'var(--bh-font-mono)',
              fontSize: 12, fontWeight: 700,
              color: 'var(--bh-ink-mute)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              paddingTop: 4,
            }}>{mo.m}</div>
            <div style={{ paddingTop: 6 }}>
              <span style={{
                display: 'block', width: 14, height: 14, borderRadius: '50%',
                background: moodColors[mo.mood],
                boxShadow: '0 0 0 4px rgba(0,0,0,.03)',
              }} />
            </div>
            <div>
              <h3 style={{
                fontFamily: 'var(--bh-font-display)', fontSize: 20, fontWeight: 600,
                letterSpacing: '-0.02em', margin: '0 0 4px', color: 'var(--bh-ink)',
              }}>{mo.t}</h3>
              <p style={{ fontSize: 14.5, color: 'var(--bh-ink-soft)', lineHeight: 1.55, margin: 0 }}>{mo.d}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ═══ THE ICEBERG ═══
// What "just a Slack bot" actually contains.
function BvBIceberg() {
  const above = [
    'Send a Slack message on a birthday',
    'Post a kudos card in #shoutouts',
    'A weekly pulse question',
  ];
  const below = [
    ['Identity', ['SSO with Slack & Teams', 'Multi-workspace + multi-tenant', 'User lifecycle on HRIS sync', 'De-provisioning on offboarding']],
    ['HRIS sync', ['BambooHR / Personio / Rippling / HiBob / Workday connectors', 'Field mapping UI', 'Rate-limited backfills', 'Conflict resolution on overlapping sources']],
    ['Scheduling', ['Cron in N time-zones', 'Skip company holidays per country', 'Quiet hours', 'Retry on Slack 5xx & re-deliver']],
    ['Content & UI', ['Block Kit templates', 'Teams Adaptive Cards (different language!)', 'Internationalisation in 6+ languages', 'A back-office UI for HR to edit copy']],
    ['Data & privacy', ['Postgres + backups + PITR', 'GDPR data export & right-to-be-forgotten', 'EU vs US data residency', 'Audit log for HR-visible actions']],
    ['Reliability', ['Status page', 'Alerting on missed birthdays', 'On-call rotation', 'Slack & Teams deprecation tracking']],
    ['Distribution', ['Slack App Directory review', 'Microsoft AppSource submission', 'Marketplace screenshots, privacy policy, support email']],
    ['Support', ['"It didn\'t post" debugging', 'Onboarding new admins', 'Training new HR hires every quarter']],
  ];
  return (
    <section className="bh-section" style={{
      background: 'var(--bh-ink)',
      color: 'var(--bh-cream)',
      borderTop: '1px solid rgba(255,248,238,.1)',
      borderBottom: '1px solid rgba(255,248,238,.1)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 80% 20%, rgba(255,159,28,.12), transparent 55%)',
        pointerEvents: 'none',
      }} />
      <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 48px' }}>
          <div className="bh-chip" style={{ background: 'rgba(255,159,28,.15)', borderColor: 'rgba(255,159,28,.4)', color: 'var(--bh-sun-300)', marginBottom: 14 }}>🧊 The iceberg</div>
          <h2 style={{ fontSize: 'clamp(30px, 4.2vw, 50px)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0, color: 'var(--bh-cream)' }}>
            What "just an engagement app"<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-sun-300)' }}>is actually made of.</span>
          </h2>
        </div>

        {/* Above the waterline */}
        <div style={{
          maxWidth: 720, margin: '0 auto 4px',
          background: 'rgba(255,248,238,.06)',
          border: '1px solid rgba(255,248,238,.15)',
          borderRadius: 'var(--bh-r-lg)',
          padding: '22px 26px',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
            color: 'var(--bh-sun-300)', marginBottom: 12,
          }}>↑ What gets pitched</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
            {above.map((a, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 15, color: 'var(--bh-cream)' }}>
                <span style={{ color: 'var(--bh-sun-300)' }}>•</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Waterline */}
        <div style={{
          maxWidth: 880,
          textAlign: 'center',
          fontFamily: 'var(--bh-font-mono)', fontSize: 11,
          color: 'rgba(255,248,238,.5)',
          letterSpacing: '0.1em', textTransform: 'uppercase',
          padding: '14px 0',
          borderTop: '1px dashed rgba(255,248,238,.2)',
          borderBottom: '1px dashed rgba(255,248,238,.2)',
          margin: '10px auto',
        }}>≈≈≈ waterline · everything below is also you ≈≈≈</div>

        {/* Below the waterline */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14,
        }}>
          {below.map(([title, items]) => (
            <div key={title} style={{
              background: 'rgba(255,248,238,.04)',
              border: '1px solid rgba(255,248,238,.1)',
              borderRadius: 'var(--bh-r-md, 14px)',
              padding: '18px 18px',
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
                color: 'var(--bh-sun-300)', marginBottom: 10,
              }}>{title}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                {items.map((it, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13.5, color: 'rgba(255,248,238,.8)', lineHeight: 1.45 }}>
                    <span style={{ color: 'rgba(255,248,238,.4)' }}>·</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p style={{
          maxWidth: 720, margin: '40px auto 0', textAlign: 'center',
          fontFamily: 'var(--bh-font-display)', fontStyle: 'italic',
          fontSize: 18, lineHeight: 1.5, color: 'rgba(255,248,238,.7)',
        }}>
          BuddiesHR is the iceberg, productised, and split into nine apps so you only pay for the ones you turn on.
        </p>
      </div>
    </section>
  );
}

// ═══ SWITCHER QUOTE ═══
function BvBSwitcherQuote() {
  return (
    <section className="bh-section" style={{ maxWidth: 920, margin: '0 auto' }}>
      <figure style={{
        margin: 0,
        background: '#fff',
        border: '1.5px solid var(--bh-line)',
        borderRadius: 'var(--bh-r-lg)',
        padding: '40px 44px',
        boxShadow: 'var(--bh-shadow-sm)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -30, right: 4, fontFamily: 'var(--bh-font-display)', fontSize: 180, color: 'var(--bh-coral-200)', opacity: .6, lineHeight: 1, fontStyle: 'italic' }}>"</div>
        <blockquote style={{
          margin: 0, position: 'relative', zIndex: 1,
          fontFamily: 'var(--bh-font-display)', fontSize: 22, fontWeight: 500,
          letterSpacing: '-0.01em', lineHeight: 1.4, color: 'var(--bh-ink)',
        }}>
          We built a kudos bot in a hackathon. It ran for 14 months. Then Slack changed Block Kit, the engineer
          who wrote it moved to platform, and HR was suddenly maintaining a Python script via Notion.{' '}
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>
            BuddiesHR was cheaper than three days of that engineer's time.
          </span>
        </blockquote>
        <figcaption style={{
          marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--bh-line)',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'var(--app-clappy)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700,
          }}>SO</div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--bh-ink)' }}>Sam Okonkwo</div>
            <div style={{ fontSize: 13, color: 'var(--bh-ink-mute)' }}>Director of People Ops · 240-person SaaS</div>
          </div>
        </figcaption>
      </figure>
    </section>
  );
}

export { BvBHero, BvBAIBuild, BvBTrueCost, BvBTimeline, BvBIceberg, BvBSwitcherQuote };
